import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LabelIcon from '@mui/icons-material/Label';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function DataCatalogHelpSection() {
  const { t } = useTranslation();
  const listTypeCards = [
    {
      id: 'criteria',
      title: t('dataCatalogHelp.listA.title'),
      hint: t('dataCatalogHelp.listA.hint'),
      examples: t('dataCatalogHelp.listA.examples', {
        returnObjects: true,
        defaultValue: ['Age > 65', 'Diagnosis: Lung Cancer', 'Blood Pressure < 90 mmHg'],
      }),
      color: 'primary',
    },
    {
      id: 'extraction',
      title: t('dataCatalogHelp.listB.title'),
      hint: t('dataCatalogHelp.listB.hint'),
      examples: t('dataCatalogHelp.listB.examples', {
        returnObjects: true,
        defaultValue: ['Heart Rate', 'Hemoglobin', 'Medication History'],
      }),
      color: 'secondary',
    },
  ];

  return (
    <Accordion sx={{ mb: 2 }} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            {t('dataCatalogHelp.title')}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('dataCatalogHelp.intro')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('dataCatalogHelp.criteriaMeaning')}
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {listTypeCards.map((card) => (
            <Paper key={card.id} variant="outlined" sx={{ p: 1.5 }}>
              <Tooltip title={card.hint} arrow placement="top-start">
                <Chip
                  icon={<LabelIcon />}
                  label={card.title}
                  color={card.color}
                  size="small"
                  sx={{ mb: 1, fontWeight: 600 }}
                />
              </Tooltip>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {card.hint}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {card.examples.map((example) => (
                  <Chip key={example} label={example} variant="outlined" size="small" />
                ))}
              </Box>
            </Paper>
          ))}
        </Stack>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {t('dataCatalogHelp.workflow')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            component={RouterLink}
            to="/catalog"
            size="small"
            variant="contained"
            startIcon={<FolderIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('dataCatalogHelp.workflowSteps.catalog')}
          </Button>
          <Button
            component={RouterLink}
            to="/cohorts"
            size="small"
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('dataCatalogHelp.workflowSteps.cohorts')}
          </Button>
          <Button
            component={RouterLink}
            to="/requests"
            size="small"
            variant="outlined"
            startIcon={<AssignmentIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('dataCatalogHelp.workflowSteps.requests')}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
